import { SelfContainedCommand, CommandExecutionContext } from '../../../types/commands';
import { StandardToolResult, createSuccessResult, createErrorResult } from '../../../types/standardToolResults';
import { schemaToParameters } from '../../../utils/schemaToParameters';

export const tasks_read: SelfContainedCommand = {
  id: 'tasks_read',
  description: `Read the user's task list. Returns tasks with status, priority, due dates, notes, and tags.

**STATUS FILTER VALUES:** active (default) | all | today | overdue | todo | in-progress | done

**EXAMPLES:**

Get active tasks:
{ }

Get overdue tasks:
{ "statusFilter": "overdue" }

**RETURNS:** Array of tasks with id, llmId, name, and skeleton (status, priority, dueDate, notes, tags).`,
  autonomyLevel: 'auto',
  visualTreatment: 'silent',

  schema: {
    parameters: schemaToParameters({
      type: 'object',
      properties: {
        statusFilter: {
          type: 'string',
          enum: ['active', 'all', 'today', 'overdue', 'todo', 'in-progress', 'done'],
          description: '"active" = non-done tasks (default). "today" and "overdue" are composite filters.',
          default: 'active',
        },
        includeCompleted: {
          type: 'boolean',
          description: 'Shorthand for statusFilter: "all". If true, includes done tasks.',
          default: false,
        },
        limit: {
          type: 'number',
          description: 'Max tasks to return (default 100)',
          default: 100,
        },
      },
      required: [],
    }),
  },

  examples: { good: [], bad: [] },

  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const { statusFilter = 'active', limit = 30, includeCompleted = false } = params;

    try {
      const { default: useEntityStore } = await import('../../../stores/entityStore');
      const entities = useEntityStore.getState().entities;
      const today = new Date().toISOString().split('T')[0];

      let tasks = entities
        .filter((e: any) => e.entityType === 'Task')
        .map((e: any) => ({
          id: e._id || e.id,
          llmId: e.llmId,
          name: e.name,
          notes: e.skeleton?.notes || null,
          status: e.skeleton?.status || 'todo',
          completed: e.skeleton?.completed || false,
          completedAt: e.skeleton?.completedAt || null,
          dueDate: e.skeleton?.dueDate || null,
          priority: e.skeleton?.priority || 'medium',
          isInToday: e.skeleton?.isInToday || false,
          tags: e.skeleton?.tags || [],
          projectId: e.skeleton?.projectId || null,
          areaId: e.skeleton?.areaId || null,
        }));

      if (!includeCompleted) {
        tasks = tasks.filter((t) => t.status !== 'done' && t.status !== 'canceled');
      }

      switch (statusFilter) {
        case 'active':
          tasks = tasks.filter((t) => t.status === 'todo' || t.status === 'in-progress');
          break;
        case 'today':
          tasks = tasks.filter((t) => t.isInToday || t.dueDate === today);
          break;
        case 'overdue':
          tasks = tasks.filter(
            (t) =>
              t.dueDate &&
              t.dueDate < today &&
              t.status !== 'done' &&
              t.status !== 'canceled'
          );
          break;
        case 'all':
          break;
        default:
          tasks = tasks.filter((t) => t.status === statusFilter);
      }

      const priorityOrder: Record<string, number> = { high: 0, medium: 1, low: 2 };
      tasks.sort((a, b) => {
        const aOverdue = a.dueDate && a.dueDate < today ? -1 : 0;
        const bOverdue = b.dueDate && b.dueDate < today ? -1 : 0;
        if (aOverdue !== bOverdue) return aOverdue - bOverdue;
        if (a.dueDate && b.dueDate) return a.dueDate.localeCompare(b.dueDate);
        if (a.dueDate) return -1;
        if (b.dueDate) return 1;
        return (priorityOrder[a.priority] ?? 1) - (priorityOrder[b.priority] ?? 1);
      });

      tasks = tasks.slice(0, limit);

      return createSuccessResult(`Read ${tasks.length} tasks`, {
        tasks,
        count: tasks.length,
        today,
      });
    } catch (error: any) {
      return createErrorResult(
        'Failed to read tasks',
        error.message || 'Unknown error',
        'execution'
      );
    }
  },
};
